import Axios from 'axios';
import React, { useContext, useEffect, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingBox from '../component/LoadingBox';
import MessageBox from '../component/MessageBox';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import { Store } from '../Store';
import { getError } from '../utils';
import * as htmlToImage from 'html-to-image';
import { jsPDF } from 'jspdf';
import '../assets/invoice-new.css';
import { api } from '../Api';

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, order: action.payload, error: '' };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function InvoiceCopy({ order, label }) {
  const items = order.orderItems || [];
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const shipping = Number(order.shippingPrice) || 0;
  const total = Number(order.totalPrice) || subtotal + shipping;

  return (
    <section className="inv2-copy">
      <header className="inv2-header">
        <div className="inv2-brand">
          
          <div className="inv2-brand-meta">
            <div className="inv2-brand-name"><img
            src="https://res.cloudinary.com/dddogyqqa/image/upload/v1670435815/logo/clickcart-logo_wfktlg.png"
            alt="ClickCart"
            className="inv2-logo"
            crossOrigin="anonymous"
          /></div>
            <div className="inv2-brand-addr">
              JA-80/b, Bir Uttam A.K Khondokar Road, Badda Link Road, Badda, Dhaka
            </div>
          </div>
        </div>
        <div className="inv2-title-block">
          <div className="inv2-title">INVOICE</div>
          <div className="inv2-copy-label">{label}</div>
          <div className="inv2-id">#{order._id.slice(-8).toUpperCase()}</div>
        </div>
      </header>

      <div className="inv2-meta-row">
        <div className="inv2-meta-block">
          <div className="inv2-meta-label">Billed To</div>
          <div className="inv2-meta-value inv2-customer-name">
            {order.shippingAddress?.fullName || order.user?.name || '—'}
          </div>
          <div className="inv2-meta-sub">
            @{order.user?.username || '—'}
            {order.shippingAddress?.phone && (
              <> · {order.shippingAddress.phone}</>
            )}
          </div>
          <div className="inv2-meta-sub">
            {order.shippingAddress?.streetAddress || '—'}
          </div>
        </div>
        <div className="inv2-meta-block inv2-meta-right">
          <div className="inv2-meta-row-pair">
            <div>
              <div className="inv2-meta-label">Invoice Date</div>
              <div className="inv2-meta-value">{formatDate(order.createdAt)}</div>
            </div>
            <div>
              <div className="inv2-meta-label">Due Date</div>
              <div className="inv2-meta-value">{formatDate(order.createdAt)}</div>
            </div>
          </div>
          <div className="inv2-meta-row-pair">
            <div>
              <div className="inv2-meta-label">Payment</div>
              <div className="inv2-meta-value">{order.paymentMethod || '—'}</div>
            </div>
            <div>
              <div className="inv2-meta-label">Status</div>
              <div className={`inv2-status ${order.isPaid ? 'inv2-status-paid' : 'inv2-status-due'}`}>
                {order.isPaid ? 'PAID' : 'DUE'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <table className="inv2-items">
        <thead>
          <tr>
            <th className="inv2-col-idx">#</th>
            <th className="inv2-col-desc">Description</th>
            <th className="inv2-col-num">Price</th>
            <th className="inv2-col-num">Qty</th>
            <th className="inv2-col-num">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={item.slug || idx}>
              <td className="inv2-col-idx">{idx + 1}</td>
              <td className="inv2-col-desc">{item.name}</td>
              <td className="inv2-col-num">{formatMoney(item.price)}</td>
              <td className="inv2-col-num">{item.quantity}</td>
              <td className="inv2-col-num">{formatMoney(item.price * item.quantity)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="inv2-totals-wrap">
        <div className="inv2-totals">
          <div className="inv2-totals-row">
            <span>Subtotal</span>
            <span>{formatMoney(subtotal)} TK</span>
          </div>
          <div className="inv2-totals-row">
            <span>Shipping</span>
            <span>{formatMoney(shipping)} TK</span>
          </div>
          <div className="inv2-totals-row inv2-totals-grand">
            <span>Total</span>
            <span>{formatMoney(total)} TK</span>
          </div>
        </div>
      </div>

      <footer className="inv2-footer">
        <div className="inv2-sign">
          <div className="inv2-sign-line" />
          <div className="inv2-sign-label">Receiver</div>
        </div>
        <div className="inv2-sign">
          <div className="inv2-sign-line" />
          <div className="inv2-sign-label">Accounts</div>
        </div>
        <div className="inv2-sign">
          <div className="inv2-sign-line" />
          <div className="inv2-sign-label">Director</div>
        </div>
      </footer>
    </section>
  );
}

export default function InvoicePDFNew() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const { id: orderId } = useParams();
  const navigate = useNavigate();

  const [{ loading, error, order }, dispatch] = useReducer(reducer, {
    loading: true,
    order: {},
    error: '',
  });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!userInfo) {
      toast.error('Please Login');
      navigate('/signin');
      return;
    }
    const fetchOrder = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await Axios.get(`${api}/api/orders/${orderId}`, {
          headers: { authorization: `Bearer ${userInfo.token}` },
        });
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    fetchOrder();
  }, [orderId, userInfo, navigate]);

  async function preloadImages(root) {
    if (!root) return;
    const imgs = Array.from(root.querySelectorAll('img'));
    await Promise.all(
      imgs.map(
        (img) =>
          new Promise((resolve) => {
            try {
              if (!img.src) return resolve();
              if (!img.crossOrigin) img.crossOrigin = 'anonymous';
              if (img.complete && img.naturalHeight > 0) return resolve();
              img.onload = () => resolve();
              img.onerror = () => resolve();
              const src = img.src;
              img.src = '';
              img.src = src;
            } catch {
              resolve();
            }
          })
      )
    );
  }

  async function downloadPdf() {
    const node = document.getElementById('inv2-page');
    if (!node) {
      toast.error('Invoice not ready.');
      return;
    }
    setDownloading(true);
    try {
      await preloadImages(node);
      const dataUrl = await htmlToImage.toPng(node, {
        quality: 0.98,
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
      });
      const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(dataUrl);
      const ratio = Math.min(pageWidth / imgProps.width, pageHeight / imgProps.height);
      const w = imgProps.width * ratio;
      const h = imgProps.height * ratio;
      const x = (pageWidth - w) / 2;
      const y = (pageHeight - h) / 2;
      pdf.addImage(dataUrl, 'PNG', x, y, w, h);
      pdf.save(`Invoice_${order._id}.pdf`);
      toast.success('Invoice downloaded.');
    } catch (err) {
      toast.info('Opening print dialog — choose "Save as PDF".');
      window.print();
    } finally {
      setDownloading(false);
    }
  }

  function printInvoice() {
    window.print();
  }

  if (loading) return <LoadingBox />;
  if (error) return <MessageBox variant="danger">{error}</MessageBox>;
  if (!order || !order._id) return <MessageBox variant="warning">Order not found</MessageBox>;

  return (
    <div className="inv2-wrap">
      <Helmet>
        <title>Invoice #{order._id.slice(-8).toUpperCase()}</title>
      </Helmet>
      <div className="inv2-actions d-print-none">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => window.history.back()}
        >
          ← Back
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={printInvoice}
          disabled={downloading}
        >
          Print
        </Button>
        <Button
          variant="success"
          size="sm"
          onClick={downloadPdf}
          disabled={downloading}
        >
          {downloading ? (
            <><Spinner animation="border" size="sm" /> Preparing…</>
          ) : (
            'Download PDF'
          )}
        </Button>
      </div>
      <div className="inv2-page" id="inv2-page">
        <InvoiceCopy order={order} label="Customer Copy" />
        <div className="inv2-cutline" aria-hidden="true">
          <span>✂</span>
        </div>
        <InvoiceCopy order={order} label="Office Copy" />
      </div>
    </div>
  );
}
